import {
  INTL_ROUTE_LABELS,
  INTL_ROUTES,
  allocationProblems,
  flatFreightMinor,
  hasFlatFreightTable,
  isMcfServable,
  isShopifyRoute,
  routesUsed,
  unitsForRoute,
  type IntlAllLine,
  type IntlAllocation,
  type IntlAllRequest,
  type IntlDeviceModel,
  type IntlRoute,
} from '@staff-app/shared-types';

/**
 * The Route dialog's state: one text cell per (model, route). Text, not numbers, so a half-typed
 * cell stays what the operator typed instead of snapping to 0 under their cursor.
 */
export type RouteGrid = Partial<Record<IntlDeviceModel, Partial<Record<IntlRoute, string>>>>;

/** The grid a request opens with: its saved allocations, or nothing when it is not routed yet. */
export function gridFromAllocations(allocations: readonly IntlAllocation[]): RouteGrid {
  const grid: RouteGrid = {};
  for (const a of allocations) {
    grid[a.deviceModel] = { ...(grid[a.deviceModel] ?? {}), [a.route]: String(a.quantity) };
  }
  return grid;
}

/**
 * The allocations the grid says, ready for `allocationProblems` and the mutation. Empty cells and
 * 0 are "not this route"; anything else is passed through as typed (a `1.5` becomes 1.5 so the
 * shared check names it rather than this quietly rounding it).
 */
export function allocationsFromGrid(grid: RouteGrid): IntlAllocation[] {
  const out: IntlAllocation[] = [];
  for (const [model, cells] of Object.entries(grid) as [IntlDeviceModel, Partial<Record<IntlRoute, string>>][]) {
    for (const route of INTL_ROUTES) {
      const raw = (cells?.[route] ?? '').trim();
      if (!raw) continue;
      const quantity = Number(raw);
      if (quantity === 0) continue;
      out.push({ deviceModel: model, route, quantity });
    }
  }
  return out;
}

export interface RouteEligibility {
  mcf: readonly IntlDeviceModel[];
  shopify: readonly IntlDeviceModel[];
}

/** May this model ship by this route, per the Products List ticks? */
export function routeAllowed(model: IntlDeviceModel, route: IntlRoute, el: RouteEligibility): boolean {
  return isShopifyRoute(route) ? el.shopify.includes(model) : el.mcf.includes(model);
}

/**
 * Every reason the grid cannot be saved: the shared allocation check (sums, duplicates, whole
 * numbers) plus the Products List ticks, which only the client can phrase helpfully — the server
 * refuses too, but "tick it on the Products List" is the sentence that gets it fixed.
 */
export function routeGridProblems(
  lines: readonly IntlAllLine[],
  allocations: readonly IntlAllocation[],
  el: RouteEligibility,
  /** The ship-to country. Amazon MCF reaches only the US, the UK and the EU-27 (`isMcfServable`). */
  country?: string | null,
): string[] {
  const problems = allocationProblems(lines, allocations);
  if (country != null && !isMcfServable(country) && allocations.some((a) => a.route === 'MCF' && a.quantity > 0)) {
    problems.push(
      `Amazon MCF doesn't ship to ${country}.`,
    );
  }
  for (const a of allocations) {
    if (!routeAllowed(a.deviceModel, a.route, el)) {
      problems.push(
        `${a.deviceModel} is not enabled for ${INTL_ROUTE_LABELS[a.route]} — tick ` +
          `"${isShopifyRoute(a.route) ? 'China / Houston' : 'Amazon MCF'}" for it on the Products List.`,
      );
    }
  }
  return problems;
}

export interface ShipmentPreview {
  route: IntlRoute;
  units: number;
  /** Minor units from the flat table (China / Houston) — in `currency` when it has a table, else USD; null for MCF. */
  flatUsdMinor: number | null;
}

/**
 * One row per shipment the grid would create, with China / Houston freight computed LIVE from the
 * same `flatFreightMinor` the invoice uses — so the dialog cannot show $70 above an invoice for $80.
 * Invalid quantities are skipped here (the problem list already names them).
 */
export function shipmentPreviews(allocations: readonly IntlAllocation[], currency = 'usd'): ShipmentPreview[] {
  const table = hasFlatFreightTable(currency) ? currency : 'usd';
  const valid = allocations.filter((a) => Number.isInteger(a.quantity) && a.quantity > 0);
  return routesUsed(valid).map((route) => {
    const units = unitsForRoute(valid, route);
    const count = Object.values(units).reduce((n, q) => n + (q ?? 0), 0);
    return {
      route,
      units: count,
      flatUsdMinor: route === 'CHINA' || route === 'HOUSTON' ? flatFreightMinor(units, route, table) : null,
    };
  });
}

/** "10 × Pro, 4 × OptiStick" — the request's lines in one cell. */
export function linesSummary(
  lines: readonly IntlAllLine[],
  labels: Readonly<Record<IntlDeviceModel, string>>,
): string {
  return lines.map((l) => `${l.quantity} × ${labels[l.deviceModel] ?? l.deviceModel}`).join(', ');
}

/**
 * A live quote or any invoice freezes the routing — the price was built from it. Mirrors the
 * service's refusal so the button is not offered for a save that would be refused.
 */
export function routingFrozenReason(
  r: Pick<IntlAllRequest, 'stripeInvoiceId' | 'stripeQuoteId' | 'quoteStatus' | 'quoteExpired'>,
): string | null {
  if (r.stripeInvoiceId) return 'Invoiced — the routing is what the invoice billed, so it can no longer change.';
  // An expired quote binds nothing — the server retires it when the routing is saved.
  if (r.stripeQuoteId && r.quoteStatus !== 'canceled' && !r.quoteExpired) {
    return 'Quoted — cancel the quote before re-routing, because the quote was priced from this routing.';
  }
  return null;
}
